export function initDropdown() : void {
    document.querySelectorAll<HTMLElement>('[data-dropdown]').forEach($toggler => {
        const targetSelector = $toggler.dataset.dropdown;

        if (!targetSelector) {
            return;
        }

        const $target = document.querySelector<HTMLElement>(targetSelector);

        if (!$target) {
            return;
        }

        new Dropdown($toggler, $target);
    });
}

export const openDropdownEvent = 'open_dropdown';
export const closeDropdownEvent = 'close_dropdown';

/**
 * Make a dropdown
 * 
 * Usage:
 * 
 * <div id="my-container">
 *      <button
 *          data-dropdown="#my-dropdown"
 *          data-dropdown-container="#my-container" <== Optional
 *          data-dropdown-scroll-in-view            <== Optional
 *      >
 *          Button toggler
 *      </button>
 * 
 *      <div id="my-dropdown">
 *          Dropdown content
 *      </div>
 * </div>
 */
class Dropdown {
    $toggler: HTMLElement;
    $dropdown: HTMLElement;
    containerSelector: string|null;
    scrollInView: boolean;
    isOpen = false;

    constructor($toggler: HTMLElement, $dropdown: HTMLElement) {
        this.$toggler = $toggler;
        this.$dropdown = $dropdown;
        this.containerSelector = this.$toggler.dataset.dropdownContainer ?? null;
        this.scrollInView = ['', 'true'].includes(this.$toggler.dataset.dropdownScrollInView ?? 'false');

        this.hideDropdown();

        this.$toggler.addEventListener('click', () => {
            this.toggleDropdown();
        });

        if (this.containerSelector) {
            this.hideWhenClickedOutside();
        }
    }

    /**
     * Show the dropdown
     * 
     * @return {void}
     */
    showDropdown() : void {
        this.$dropdown.classList.remove('hidden');
        this.isOpen = true;

        if (this.scrollInView) {
            this.scrollToToggler();
        }

        this.$toggler.dispatchEvent(new CustomEvent(openDropdownEvent));
    }

    /**
     * Hide the dropdown
     * 
     * @return {void}
     */
    hideDropdown() : void {
        this.$dropdown.classList.add('hidden');
        this.isOpen = false;

        this.$toggler.dispatchEvent(new CustomEvent(closeDropdownEvent));
    }

    /**
     * Toggle the dropdown
     * 
     * @return {void}
     */
    toggleDropdown() : void {
        if (this.isOpen) {
            this.hideDropdown();
        } else {
            this.showDropdown();
        }
    }

    /**
     * Hide the dropdown when the user click outside the container
     * 
     * @return {void}
     */
    hideWhenClickedOutside() : void {
        document.addEventListener('click', evt => {
            if (!this.isOpen) {
                return;
            }

            if (this.containerSelector && !(evt.target as HTMLElement).closest(this.containerSelector)) {
                this.hideDropdown();
            }
        });
    }

    /**
     * Scroll to the Toggler
     * 
     * @return {void}
     */
    scrollToToggler() : void {
        // Include the navbar and the toggler in the scroll-padding-top css property
        document.documentElement.style.scrollPaddingTop = (100 + this.$toggler.clientHeight) + 'px';
        document.documentElement.style.scrollPaddingBottom = '15px';

        this.$dropdown.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
        });

        document.documentElement.style.scrollPaddingTop = '';
        document.documentElement.style.scrollPaddingBottom = '';
    }
}
